import React, { useEffect, useState } from "react";
import { Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ConfirmationModal, FormGroupContainer, FormModal, NumberField } from "@packsize/components";
import { useIntl } from "react-intl";

import {
	type CartonPropertyGroupConfiguration,
	cartonPropertyGroupConfigurationSchema,
} from "@/schemas/carton-property-group";
import { type ProductionGroup } from "@/schemas/production-group";

export interface CartonPropertyGroupsConfigurationModalTemplateProps {
	readonly open: boolean;
	readonly defaultValues?: CartonPropertyGroupConfiguration;
	readonly productionGroups: ProductionGroup[] | null | undefined;
	readonly errorMessage?: string;
	readonly onSubmit: (data: CartonPropertyGroupConfiguration) => Promise<void>;
	readonly onClose: () => void;
}

export const CartonPropertyGroupsConfigurationModalTemplate: React.FC<
	CartonPropertyGroupsConfigurationModalTemplateProps
> = ({ open, defaultValues, productionGroups, errorMessage, onClose, onSubmit }) => {
	const intl = useIntl();
	const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors, isDirty, isSubmitting },
	} = useForm<CartonPropertyGroupConfiguration>({
		resolver: zodResolver(cartonPropertyGroupConfigurationSchema(intl)),
		defaultValues: defaultValues ?? { alias: "", ratio: 1, assignedToProductionGroups: [] },
	});

	useEffect(() => {
		if (open !== false) {
			return;
		}

		reset(defaultValues);
	}, [defaultValues, open, reset]);

	const handleRequestClose = (): void => {
		if (isDirty) {
			setConfirmLeaveOpen(true);
			return;
		}

		onClose();
	};

	return (
		<>
			<ConfirmationModal
				open={confirmLeaveOpen}
				onClose={() => setConfirmLeaveOpen(false)}
				onCancel={() => setConfirmLeaveOpen(false)}
				onConfirm={() => {
					setConfirmLeaveOpen(false);
					onClose();
				}}
				title={intl.formatMessage({ id: "Leave without saving?", defaultMessage: "Leave without saving?" })}
				confirmText={intl.formatMessage({ id: "Leave", defaultMessage: "Leave" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				size="sm"
				slotProps={{
					confirmButton: {
						"data-testid": "carton-property-groups-configuration-modal__button__confirm-leave",
						"data-pendoid": "carton-property-groups-configuration-modal__button__confirm-leave",
					},
					cancelButton: {
						"data-testid": "carton-property-groups-configuration-modal__button__cancel-leave",
						"data-pendoid": "carton-property-groups-configuration-modal__button__cancel-leave",
					},
				}}
			>
				<div>
					{intl.formatMessage({ id: "All unsaved work will be lost", defaultMessage: "All unsaved work will be lost" })}
				</div>
			</ConfirmationModal>
			<FormModal
				open={open}
				title={
					defaultValues
						? intl.formatMessage({ id: "Edit Carton Property Group", defaultMessage: "Edit Carton Property Group" })
						: intl.formatMessage({ id: "Create Carton Property Group", defaultMessage: "Create Carton Property Group" })
				}
				onSubmit={handleSubmit(onSubmit)}
				size="lg"
				submitText={intl.formatMessage({ id: "Save", defaultMessage: "Save" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				onClose={handleRequestClose}
				onCancel={handleRequestClose}
				slotProps={{
					confirmButton: {
						loading: isSubmitting,
						"data-testid": "carton-property-groups-configuration-modal__button__confirm",
						"data-pendoid": "carton-property-groups-configuration-modal__button__confirm",
					},
					cancelButton: {
						"data-testid": "carton-property-groups-configuration-modal__button__cancel",
						"data-pendoid": "carton-property-groups-configuration-modal__button__cancel",
					},
				}}
			>
				<Grid display="grid" gridTemplateColumns="1fr 1fr" gap={2} data-cy="add-form">
					<Stack gap={2}>
						<TextField
							label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
							fullWidth
							error={Boolean(errors?.alias)}
							helperText={errors?.alias?.message}
							{...register("alias")}
							sx={{ mt: 1.5 }}
							slotProps={{
								htmlInput: {
									"data-testid": "carton-property-groups-configuration-modal__input__alias",
									"data-pendoid": "carton-property-groups-configuration-modal__input__alias",
								},
								formHelperText: {
									"data-testid": "carton-property-groups-configuration-modal__input__alias-error",
								},
							}}
						/>
						<Controller
							name="ratio"
							control={control}
							render={({ field }) => (
								<NumberField
									label={intl.formatMessage({ id: "Ratio", defaultMessage: "Ratio" })}
									value={Number(field.value ?? 1)}
									min={1}
									onChange={(num) => field.onChange(num ?? 1)}
									data-testid="carton-property-groups-configuration-modal__number-field__ratio"
									data-pendoid="carton-property-groups-configuration-modal__number-field__ratio"
								/>
							)}
						/>
					</Stack>

					<Controller
						name="assignedToProductionGroups"
						control={control}
						render={({ field }) => (
							<FormGroupContainer
								legend={intl.formatMessage({ id: "Production Groups", defaultMessage: "Production Groups" })}
								sx={{ minHeight: 300, py: 1 }}
							>
								<Stack>
									{productionGroups?.map((productionGroup) => (
										<FormControlLabel
											key={productionGroup.id}
											control={
												<Checkbox
													checked={(field.value ?? []).includes(productionGroup.id)}
													onChange={(event) => {
														const currentAssigned = field.value ?? [];
														if (event.target.checked) {
															field.onChange([...currentAssigned, productionGroup.id]);
														} else {
															field.onChange(currentAssigned.filter((i) => i !== productionGroup.id));
														}
													}}
													data-testid={`carton-property-groups-configuration-modal__checkbox__production-group-${productionGroup.id}`}
													data-pendoid="carton-property-groups-configuration-modal__checkbox__production-group"
												/>
											}
											label={productionGroup.alias}
										/>
									))}
								</Stack>
							</FormGroupContainer>
						)}
					/>
					{Boolean(errorMessage) === true && (
						<Typography
							color="error"
							whiteSpace="pre-wrap"
							data-testid="carton-property-groups-configuration-modal__save__error"
						>
							{errorMessage}
						</Typography>
					)}
				</Grid>
			</FormModal>
		</>
	);
};
